import vuetify from "@/plugins/vuetify";
import pinia from "@/stores";
import { i18n } from "@/locales";
import type { App } from "vue";
import { VueQueryPlugin } from "@tanstack/vue-query";
import { queryClient } from "@/lib/queryClient";
import { abilitiesPlugin } from "@casl/vue";
import { ability, ESubjects, PermActions } from "@/api/models/auth/ability";
import { useAuthStore } from "@/stores/authStore";
import { GridLayout, GridItem } from "grid-layout-plus";
import { setupRouterGuards, closeSessionGate, openSessionGate } from "@/router/guards";
import { router } from "@/router";
import VueKonva from "vue-konva";

export const registerPlugins = (app: App): void => {
  app.config.globalProperties.$permSubjects = ESubjects;
  app.config.globalProperties.$permActions = PermActions;

  (globalThis as any).$permSubjects = ESubjects;
  (globalThis as any).$permActions = PermActions;

  setupRouterGuards(router, i18n);

  // NB: `router` is deliberately NOT installed here — see
  // revalidateSessionAndInstallRouter() below.
  app
    .use(vuetify)
    .use(pinia)
    .use(i18n)
    .use(VueQueryPlugin, { queryClient, enableDevtoolsV6Plugin: true })
    .use(abilitiesPlugin, ability as Parameters<typeof abilitiesPlugin>[1], {
      useGlobalProperties: true,
    })
    .use(VueKonva, { prefix: "Konva" });

  app.component("GridLayout", GridLayout).component("GridItem", GridItem);
};

/**
 * Installs the router and revalidates the session against /me before any
 * privilege check runs.
 *
 * Two constraints pull in opposite directions:
 *
 * 1. The auth store's setup calls useRouter()/useRoute(), so it can only be
 *    created after `app.use(router)` — creating it earlier leaves `router`
 *    undefined and logout dies on `router.push`.
 * 2. But `app.use(router)` immediately starts the initial navigation, which
 *    would evaluate privileges against the stale persisted snapshot. The check
 *    can't move into the guard either: an `await` there drops Vue Router's
 *    injection context, which `checkPrivileges` needs.
 *
 * Hence the gate: the guard holds the first navigation synchronously (see
 * closeSessionGate/openSessionGate) while /me is fetched here.
 *
 * `router.isReady()` is deliberately NOT awaited. The gate answers the initial
 * navigation with `next(false)`, and an aborted navigation never lets isReady()
 * settle — while the caller mounts only once this resolves. Awaiting it here
 * deadlocks boot: empty `#app`, no error logged, every Playwright auth setup
 * failing on a login form that was never rendered. The gate's own
 * `router.replace` re-dispatches the navigation after mount.
 *
 * A failure must not block boot — it falls through to the normal
 * unauthenticated/expired-session handling.
 */
export const revalidateSessionAndInstallRouter = async (app: App): Promise<void> => {
  // Close the gate BEFORE installing the router: installing starts the initial
  // navigation, and the guard must already be holding it back by then.
  closeSessionGate();
  app.use(router);

  // Safe now — the router is installed, so the store's useRouter()/useRoute()
  // resolve, while the gate keeps the guard from reading a stale snapshot.
  const authStore = useAuthStore();
  try {
    await authStore.ensureAuthReady();
  } catch (error) {
    logError(error, {
      context: { feature: "auth", step: "revalidateSession" },
    });
  }

  openSessionGate();
};
